import Image from 'next/image';
import {
  MENU_PATH_DELIMITER,
  trackMegaMenuBanner,
} from '_utils/GA4/trackMegaMenuEvents';
import LinkButton from '_components/UI/LinkButton';
import styles from './FeatureCard.module.scss';
import type { FeatureCard as FeatureCardType } from '../types';

interface FeatureCardProps {
  card: FeatureCardType;
  wide?: boolean;
  sideBySide?: boolean;
  menuPathPrefix?: string;
}

export function FeatureCard({
  card,
  wide,
  sideBySide,
  menuPathPrefix = '',
}: FeatureCardProps) {
  // stacked_image (mobileImage) is the mobile variant; desktop_image (desktopImage)
  // is the desktop variant. Desktop falls back to the mobile image for legacy cards
  // that only have stacked_image set. Only render both when they actually differ.
  const desktopSrc = card.desktopImage || card.mobileImage;
  const hasDistinctImages = Boolean(
    card.desktopImage &&
      card.mobileImage &&
      card.desktopImage !== card.mobileImage
  );
  // The image box is a fixed width from lg up, but stretches to the full
  // viewport width below lg (mobile menu), so `sizes` must reflect both.
  const imageSizes = wide
    ? '(min-width: 1088px) 475px, 100vw'
    : '(min-width: 1088px) 361px, 100vw';
  const track = (linkText: string, linkUrl: string) =>
    trackMegaMenuBanner({
      linkUrl,
      linkText,
      menuPath: [menuPathPrefix, linkText]
        .filter(Boolean)
        .join(MENU_PATH_DELIMITER),
    });
  const classNames = [
    styles.FeatureCard,
    card.styleVariation === 'Black' ? styles['FeatureCard--black'] : '',
    wide ? styles['FeatureCard--wide'] : '',
    !card.links?.length && !card.secondaryLinks?.length
      ? styles['FeatureCard--no-links']
      : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={classNames}>
      <div className={styles.FeatureCardTop}>
        {desktopSrc && (
          <div className={styles.FeatureCardImageWrapper}>
            <Image
              src={desktopSrc}
              alt={card.title ?? ''}
              fill
              sizes={imageSizes}
              className={[
                styles.FeatureCardImage,
                hasDistinctImages ? styles['FeatureCardImage--desktop'] : '',
              ]
                .filter(Boolean)
                .join(' ')}
            />
            {hasDistinctImages && card.mobileImage && (
              <Image
                src={card.mobileImage}
                alt={card.title ?? ''}
                fill
                sizes={imageSizes}
                className={`${styles.FeatureCardImage} ${styles['FeatureCardImage--mobile']}`}
              />
            )}
          </div>
        )}
        <div className={styles.FeatureCardContent}>
          <div className={styles.FeatureCardTextGroup}>
            {card.title && (
              <h3 className={styles.FeatureCardTitle}>{card.title}</h3>
            )}
            {card.text && <p className={styles.FeatureCardText}>{card.text}</p>}
          </div>
          {card.cta && (
            <LinkButton
              href={card.cta.url}
              variant={card.styleVariation === 'Black' ? 'white' : 'black'}
              className={styles.FeatureCardCta}
              onClick={() => track(card.cta.label, card.cta.url)}
            >
              {card.cta.label}
            </LinkButton>
          )}
        </div>
      </div>
      {!sideBySide &&
        ((card.links?.length ?? 0) > 0 ||
          (card.secondaryLinks?.length ?? 0) > 0) && (
          <div className={styles.FeatureCardLinks}>
            {card.links && card.links.length > 0 && (
              <div className={styles.FeatureCardLinkGroup}>
                <span className={styles.FeatureCardLinksTitle}>Featured</span>
                <div className={styles.FeatureCardLinksList}>
                  {card.links.map((link, index) => (
                    <a
                      key={index}
                      href={link.url}
                      className={styles.FeatureCardLink}
                      onClick={() => track(link.label, link.url)}
                    >
                      {link.label}
                    </a>
                  ))}
                </div>
              </div>
            )}
            {card.secondaryLinks && card.secondaryLinks.length > 0 && (
              <div className={styles.FeatureCardLinkGroup}>
                <div className={styles.FeatureCardLinksList}>
                  {card.secondaryLinks.map((link, index) => (
                    <a
                      key={index}
                      href={link.url}
                      className={styles.FeatureCardLink}
                      onClick={() => track(link.label, link.url)}
                    >
                      {link.label}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
    </div>
  );
}
